import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  History,
  Person,
  CheckCircle,
  Warning,
  Info,
  Close
} from '@mui/icons-material';
import { formatDate } from '../utils/formatters';

interface AuditEvent {
  id: string;
  timestamp: Date;
  action: string;
  userId: string;
  details: string;
  status: 'success' | 'warning' | 'info' | 'error';
  metadata?: Record<string, any>;
}

interface AuditTrailViewerProps {
  open: boolean;
  fundId: string;
  onClose: () => void;
}

export const AuditTrailViewer: React.FC<AuditTrailViewerProps> = ({
  open,
  fundId,
  onClose
}) => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      loadAuditTrail();
    }
  }, [open, fundId]);

  const loadAuditTrail = async () => {
    setLoading(true);
    try {
      // TODO: Replace with actual API call
      const mockEvents: AuditEvent[] = [
        {
          id: '1',
          timestamp: new Date(),
          action: 'Reconciliation Started',
          userId: 'user123',
          details: 'Started bank reconciliation for period ending March 2024',
          status: 'info'
        },
        {
          id: '2',
          timestamp: new Date(Date.now() - 3600000),
          action: 'Transaction Matched',
          userId: 'user123',
          details: 'Matched bank transaction #1234 with book entry #5678',
          status: 'success'
        }
      ];
      setEvents(mockEvents);
    } catch (error) {
      console.error('Failed to load audit trail:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle color="success" />;
      case 'warning':
        return <Warning color="warning" />;
      case 'error':
        return <Warning color="error" />;
      default:
        return <Info color="info" />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            <History sx={{ mr: 1, verticalAlign: 'middle' }} />
            Audit Trail
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Timeline>
          {events.map((event) => (
            <TimelineItem key={event.id}>
              <TimelineOppositeContent color="textSecondary">
                {formatDate(event.timestamp)}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot color={event.status as any}>
                  {getStatusIcon(event.status)}
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle1">{event.action}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {event.details}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Tooltip title="User">
                      <Chip
                        icon={<Person />}
                        label={event.userId}
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  </Box>
                </Box>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </DialogContent>
    </Dialog>
  );
}; 